// The public funnel's server-published copy — the consent + legal fine print
// (`funnel-fine-print`) AND the marketing prose (`funnel-copy`).
//
// A module of its own rather than a section of prequalification.ts, mirroring
// partnerBrand.ts: one anonymous GET, its response type, and its fetcher, so the
// wire contract has a single home and the funnel render layer consumes it without
// redeclaring it.
//
// GET /prequalification/funnel-copy is anonymous and takes NO slug — every block
// under BOTH keys is Phoenix-tier-only server-side, so the payload cannot vary by
// partner and the Phoenix-native funnel (which resolves no brand and issues no
// brand fetch) calls the exact same URL as a branded one. The one pair of strings a
// partner CAN vary — the hero headline and subcopy — comes from the brand skin and
// is applied upstream of this payload.
//
// Published copy only: an admin's unpublished draft never appears here.

import { portalApi } from "./client";
// Type-only, so it is erased at build: prequalContent type-imports this module
// back, and a runtime import either way would be a cycle.
import type { FunnelQuestionCopy } from "../pages/prequalify/prequalContent";

/**
 * The funnel's MARKETING prose (document key `funnel-copy`) — the trust band, the
 * landing hero and role fork, and every step's question and hint.
 *
 * Nested under `PrequalFunnelCopy` rather than fetched separately: two document
 * keys with identical resolution semantics, one round trip. See the C# DTO.
 *
 * 🔴 `heroHeadline` / `heroSubcopy` are the PHOENIX tier, not the last word — a
 * partner brand's `heroHeadline` / `heroSubcopy` sit upstream and win when set.
 * Full chain: `PartnerBrandSkin.heroHeadline → this block → code default`.
 */
export interface PrequalFunnelMarketingCopy {
  // Trust band.
  trustSecurePortal: string;
  trustDuration: string;
  trustNoCreditPull: string;
  trustFree: string;
  // Landing screen.
  programOverline: string;
  heroHeadline: string;
  heroSubcopy: string;
  forkQuestionPaths: string;
  forkTitleBuyer: string;
  forkBodyBuyer: string;
  forkTitleSeller: string;
  forkBodySellerSale: string;
  // Buyer steps.
  stepTitleBuyerTarget: string;
  stepTitleBuyerCapital: string;
  stepHintBuyerCapital: string;
  stepTitleBuyerExperience: string;
  stepTitleBuyerFitCheck: string;
  stepTitleBuyerGoals: string;
  stepTitleBuyerFinalize: string;
  stepHintBuyerFinalize: string;
  // Seller steps.
  stepTitleSellerBusiness: string;
  stepTitleSellerFitCheck: string;
  stepTitleSellerNumbers: string;
  stepHintSellerNumbers: string;
  stepTitleSellerOwnerReliance: string;
  stepTitleSellerRecords: string;
  stepTitleSellerExit: string;
  stepTitleSellerFinalize: string;
  stepHintSellerFinalize: string;
  // Company & sponsor buyers — question, option and label copy (owner brief
  // 2026-10-08). Appended, in visitor order, matching the C# DTO.
  questionBuyerType: string;
  optionBuyerTypeIndividual: string;
  optionBuyerTypeCompany: string;
  optionBuyerTypeFund: string;
  labelBuyerCapitalCompany: string;
  helperBuyerCapitalCompany: string;
  /** The capital step's hint for a company buyer — no "other assets". */
  stepHintBuyerCapitalCompany: string;
  questionBuyerEquitySource: string;
  optionBuyerEquitySourceOnHand: string;
  optionBuyerEquitySourceCommitted: string;
  optionBuyerEquitySourceRaising: string;
  optionBuyerOperatorPrincipal: string;
  optionBuyerOperatorGeneralManager: string;
  optionBuyerOperatorExistingManagement: string;
  labelBuyerOperatorName: string;
  labelBuyerOperatorNameOptional: string;
  labelBuyerCompanyName: string;
  labelBuyerCompanyTitle: string;
  labelBuyerCompanyWebsite: string;
  labelBuyerCompanyAbout: string;
  helperBuyerCompanyAbout: string;
}

/** Every field is always populated — the server resolves published-override-else-
 *  code-default, so "not yet edited" is a value rather than a missing key. */
export interface PrequalFunnelCopy {
  consentLineBuyer: string;
  consentLineSeller: string;
  finePrintBuyer: string;
  finePrintSeller: string;
  indicativeLabel: string;
  administratorLine: string;
  marketing: PrequalFunnelMarketingCopy;
  /** The Buyer Consultant's certification on a telephone-call prequalification
   *  (block `call-certification-buyer`). Never rendered by the funnel; carried
   *  because every block of the key is. */
  callCertificationBuyer: string;
  /**
   * Every buyer QUESTION block of `funnel-copy` (server
   * `PrequalFunnelQuestionBlocks.All`: labels, option labels, helpers and the
   * knockout texts), as ONE map keyed by block key — published, else the code
   * default. The server sends every key; `useFunnelCopy()` still lays the map
   * over `FUNNEL_QUESTION_DEFAULTS` so a key an older server omits renders its
   * compiled default. A key published blank arrives blank (D4).
   */
  questions: FunnelQuestionCopy;
}

/**
 * Resolve the funnel's fine print.
 *
 * **Returns `null` on ANY failure, and never throws.** Deliberately unlike
 * `fetchPartnerBrand`, which distinguishes 404 from a transient error so the chrome
 * can avoid a misleading fallback notice. Here there is nothing to tell the visitor
 * and nothing for them to do: the funnel compiles the same wording in as its default,
 * so a 429, an outage or a parse failure degrades to today's copy. Surfacing an error
 * would trade a correct consent line for a scary one — and blocking finalize on it
 * would be worse still.
 */
export async function fetchPrequalFunnelCopy(): Promise<PrequalFunnelCopy | null> {
  try {
    return await portalApi.get<PrequalFunnelCopy>("/prequalification/funnel-copy");
  } catch {
    return null;
  }
}
